import type { BillingFormFields } from "../../NormasTeachingCheckout/BillingFormSection.tsx";
import {
  BonificoBusinessInformation,
  BonificoOrderDTO,
  BonificoOrderStatus,
} from "./BonificoOrderDTO.ts";
import {
  billingCountryNameOf,
  isForeignBillingCountry,
  ItalianBillingCountry,
} from "../../MoneyTransfer/billingCountries.ts";

const INVOICED_ON_PAYMENT_STATUSES = [
  BonificoOrderStatus.Paid,
  BonificoOrderStatus.Refunded,
  BonificoOrderStatus.PartiallyRefunded,
];

const NEVER_INVOICED_STATUSES = [
  BonificoOrderStatus.Draft,
  BonificoOrderStatus.Discarded,
  BonificoOrderStatus.Historical,
];

export function issuesInvoiceOnPayment(order: BonificoOrderDTO): boolean {
  return !order.b2bContract && order.invoicedOnPayment === true;
}

export function hasBonificoInvoiceBeenIssued(order: BonificoOrderDTO): boolean {
  if (order.b2bContract) return false;
  if (NEVER_INVOICED_STATUSES.includes(order.status)) return false;
  if (order.invoicedOnPayment)
    return INVOICED_ON_PAYMENT_STATUSES.includes(order.status);
  return true;
}

export function formatBusinessAddress(
  address?: BonificoBusinessInformation["businessAddress"],
): string | undefined {
  if (!address?.street) return undefined;
  if (!isForeignBillingCountry(address.country))
    return `${address.street}, ${address.postalCode} ${address.city} (${address.province})`;
  const placeParts = [address.postalCode, address.city, address.province]
    .map((part) => part?.trim())
    .filter(Boolean);
  return [
    address.street,
    placeParts.join(" "),
    billingCountryNameOf(address.country),
  ]
    .filter(Boolean)
    .join(", ");
}

export function describeBillingIdentity(billing: BillingFormFields): string {
  if (!isForeignBillingCountry(billing.address?.country))
    return `CF ${billing.taxCode?.toUpperCase() ?? ""}`;
  return `${billingCountryNameOf(billing.address?.country)}, Tax ID ${billing.vat ?? ""}`;
}

export function toBonificoBillingFormFields(
  order?: Pick<BonificoOrderDTO, "businessInformation" | "customer">,
): BillingFormFields {
  const billing = order?.businessInformation;
  return {
    invoiceRequired: !!billing,
    businessType: billing?.businessType ?? "",
    businessName: billing?.businessName ?? "",
    vat: billing?.vatCode ?? "",
    taxCode: order?.customer.taxCode ?? "",
    address: {
      street: billing?.businessAddress.street ?? "",
      cap: billing?.businessAddress.postalCode ?? "",
      city: billing?.businessAddress.city ?? "",
      province: billing?.businessAddress.province ?? "",
      country: billing?.businessAddress.country || ItalianBillingCountry.Italy,
    },
    pec: billing?.pec ?? "",
    sdiCode: billing?.sdiCode ?? "",
  };
}
